import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc, collection, deleteDoc, doc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, startAfter, where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import {
  Activity, AlertTriangle, ArrowRightLeft, Bell, BellOff, Eraser, FileText, PackageCheck, PackagePlus, PackageX, CheckCircle2, History, ShieldAlert, Wifi, WifiOff,
} from 'lucide-react';
import { analyticsDb } from '../../firebase';
import { PROBLEM_LABEL, problemValue } from '../../utils/analyticsRules';

// Health history of one device (MDM analytics T7), shown on the Logs tab above the command log.
// Reads only Firestore mdm-analitics: devices/<mac> (current state, live), the last 30 days/<yyyymmdd>
// docs and events in pages of 30 - a handful of reads per open tab, independent of fleet size.

const PAGE = 30;
const DAYS = 30;
const ICON = 16;

const asDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null);
const minutesText = (m) => {
  const v = Math.round(Number(m) || 0);
  return v >= 60 ? `${Math.floor(v / 60)} h ${v % 60} min` : `${v} min`;
};

// Belgrade calendar days, newest last, as yyyymmdd (the functions key days the same way).
function lastDays(n) {
  const [y, m, d] = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Belgrade' })
    .format(new Date()).split('-').map(Number);
  const out = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const t = new Date(Date.UTC(y, m - 1, d - i, 12));
    out.push(`${t.getUTCFullYear()}${String(t.getUTCMonth() + 1).padStart(2, '0')}${String(t.getUTCDate()).padStart(2, '0')}`);
  }
  return out;
}
const dayLabel = (k) => `${k.slice(6, 8)}.${k.slice(4, 6)}.`;

// One cell per day: what kind of day it was.
function dayTone(d) {
  if (!d) return { cls: 'bg-white/[0.04]', label: 'No data' };
  const off = d.offlineMinutes || 0;
  if (off >= 60 || (d.offlineCount || 0) >= 5) return { cls: 'bg-red-500/60', label: 'Down 1 h or more / unstable' };
  if (off > 0 || (d.offlineCount || 0) > 0 || (d.problemsSeen || []).length) return { cls: 'bg-amber-400/60', label: 'Short outage or problems' };
  return { cls: 'bg-green-500/50', label: 'OK' };
}

const appName = (pkg) => ({
  'rs.musicdj.player': 'MUSICDJ Player', 'rs.musicdj.updater': 'Updater', 'rs.musicdj.mdmupdater': 'Updater',
}[pkg] || pkg);

function eventView(e) {
  const label = PROBLEM_LABEL[e.code] || e.code;
  const value = problemValue(e);
  switch (e.type) {
    case 'offline':
      return { Icon: WifiOff, tone: 'text-gray-400', title: 'Went offline', detail: '' };
    case 'online':
      return {
        Icon: Wifi, tone: 'text-green-400', title: 'Back online',
        detail: e.offlineMinutes ? `after ${minutesText(e.offlineMinutes)}` : '',
      };
    case 'problem_start':
      return {
        Icon: e.severity === 2 ? ShieldAlert : AlertTriangle,
        tone: e.severity === 2 ? 'text-red-400' : 'text-amber-300',
        title: `${label} - ${e.severity === 2 ? 'critical' : 'warning'}`, detail: value,
      };
    case 'problem_change':
      return {
        Icon: ArrowRightLeft, tone: e.severity === 2 ? 'text-red-400' : 'text-amber-300',
        title: `${label} - now ${e.severity === 2 ? 'critical' : 'warning'}`, detail: value,
      };
    case 'problem_end':
      return { Icon: CheckCircle2, tone: 'text-green-400', title: `${label} - resolved`, detail: '' };
    case 'auto_action':
      if (e.action === 'install') {
        return {
          Icon: PackagePlus, tone: 'text-brand-400', title: `Automatic install: ${appName(e.pkg)} ${e.version}`,
          detail: e.from ? `was ${e.from}` : 'was not installed',
        };
      }
      if (e.action === 'logs') return { Icon: FileText, tone: 'text-brand-400', title: 'Logs requested automatically', detail: e.reason || '' };
      return {
        Icon: Eraser, tone: 'text-brand-400', title: 'Automatic free up space sent',
        detail: e.freeGb !== undefined ? `${e.freeGb} GB free (${e.freePct}%)` : '',
      };
    case 'auto_result':
      if (e.action === 'install') {
        const ok = e.outcome === 'ok';
        return {
          Icon: ok ? PackageCheck : PackageX, tone: ok ? 'text-green-400' : 'text-amber-300',
          title: ok ? `${appName(e.pkg)} ${e.version} installed` : `${appName(e.pkg)} ${e.version} install ${e.outcome}`,
          detail: ok ? '' : 'tried again the next time the device comes online (2 tries)',
        };
      }
      return {
        Icon: Eraser, tone: e.stopped ? 'text-amber-300' : 'text-green-400',
        title: e.stopped ? 'Free up space did not help - stopped, needs a manual check' : 'Free up space done',
        detail: `freed ${e.freedMb >= 1024 ? `${(e.freedMb / 1024).toFixed(1)} GB` : `${e.freedMb} MB`}`,
      };
    default:
      return { Icon: Activity, tone: 'text-gray-400', title: e.type || 'event', detail: '' };
  }
}

// Mute / unmute this device (mdm-analitics mutes/*, same list as the Rules page). The scan applies it
// within one scan interval. A group mute is shown but changed only on the Rules page.
function MuteControl({ mac, group }) {
  const [mutes, setMutes] = useState([]);
  const [open, setOpen] = useState(false);
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const keys = [mac, group].filter(Boolean);
    return onSnapshot(
      query(collection(analyticsDb, 'mutes'), where('key', 'in', keys)),
      (snap) => setMutes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((m) => (m.kind === 'group' ? m.key === group : m.key === mac))),
      () => setMutes([]),
    );
  }, [mac, group]);

  const active = mutes.filter((m) => !m.until || m.until > Date.now());
  const own = active.find((m) => m.kind !== 'group');
  const viaGroup = active.find((m) => m.kind === 'group');
  const shown = own || viaGroup;

  const mute = async () => {
    setBusy(true);
    try {
      await addDoc(collection(analyticsDb, 'mutes'), {
        kind: 'device',
        key: mac,
        until: until ? new Date(`${until}T23:59:59`).getTime() : null,
        reason: reason.trim(),
        createdAt: serverTimestamp(),
        createdBy: getAuth().currentUser?.email || '',
      });
      setOpen(false);
      setUntil('');
      setReason('');
      toast.success('Muted. It leaves the problem list at the next scan.');
    } catch (e) {
      toast.error('Could not mute: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  const unmute = async () => {
    setBusy(true);
    try {
      await Promise.all(active.filter((m) => m.kind !== 'group').map((m) => deleteDoc(doc(analyticsDb, 'mutes', m.id))));
      toast.success('Unmuted. It is checked again at the next scan.');
    } catch (e) {
      toast.error('Could not unmute: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  if (shown) {
    return (
      <div className="card-inset p-3 mt-3 flex items-center gap-3 flex-wrap">
        <BellOff size={ICON} className="text-gray-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-200">
            Known issue{shown.kind === 'group' ? ` - whole group ${decodeURIComponent(group)} is muted` : ''}
            {shown.reason ? <span className="text-gray-400"> · {shown.reason}</span> : null}
          </p>
          <p className="text-xs text-gray-500">
            {shown.until ? `Muted until ${new Date(shown.until).toLocaleDateString()}` : 'Muted with no end date'}
            {shown.createdBy ? ` · by ${shown.createdBy}` : ''}
          </p>
        </div>
        {own ? (
          <button type="button" disabled={busy} onClick={unmute} className="btn-secondary">
            <Bell size={ICON} /> Unmute
          </button>
        ) : (
          <span className="text-xs text-gray-500">Change it on the Rules page</span>
        )}
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="btn-secondary mt-3">
        <BellOff size={ICON} /> Mute
      </button>
    );
  }
  return (
    <div className="card-inset p-3 mt-3 grid grid-cols-1 md:grid-cols-[160px_1fr_auto_auto] gap-2 items-center">
      <input
        type="date"
        value={until}
        onChange={(e) => setUntil(e.target.value)}
        className="input-field"
        title="Muted until (empty = no end date)"
      />
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason, e.g. box in repair"
        className="input-field"
      />
      <button type="button" disabled={busy} onClick={mute} className="btn-primary">
        <BellOff size={ICON} /> Mute
      </button>
      <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
    </div>
  );
}

const FILTERS = [
  ['all', 'All'],
  ['connection', 'Connection'],
  ['problems', 'Problems'],
  ['automatic', 'Automatic'],
];

export default function DeviceHistory({ mac }) {
  const [state, setState] = useState(undefined);
  const [days, setDays] = useState({});
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const deviceRef = useMemo(() => (mac ? doc(analyticsDb, 'devices', mac) : null), [mac]);

  useEffect(() => {
    if (!deviceRef) return undefined;
    return onSnapshot(deviceRef, (s) => setState(s.exists() ? s.data() : null), () => setState(null));
  }, [deviceRef]);

  useEffect(() => {
    if (!deviceRef) return;
    let alive = true;
    getDocs(query(collection(deviceRef, 'days'), orderBy('day', 'desc'), limit(DAYS)))
      .then((snap) => alive && setDays(Object.fromEntries(snap.docs.map((d) => [d.id, d.data()]))))
      .catch(() => {});
    return () => { alive = false; };
  }, [deviceRef]);

  const loadEvents = async (after) => {
    if (!deviceRef) return;
    setLoading(true);
    try {
      const q = after
        ? query(collection(deviceRef, 'events'), orderBy('at', 'desc'), startAfter(after), limit(PAGE))
        : query(collection(deviceRef, 'events'), orderBy('at', 'desc'), limit(PAGE));
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEvents((prev) => (after ? [...prev, ...rows] : rows));
      setCursor(snap.docs[snap.docs.length - 1] || null);
      setMore(snap.docs.length === PAGE);
    } catch (e) {
      setMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setEvents([]); loadEvents(null); }, [deviceRef]); // eslint-disable-line

  const keys = useMemo(() => lastDays(DAYS), []);
  const totals = keys.reduce((a, k) => {
    const d = days[k];
    if (!d) return a;
    return { count: a.count + (d.offlineCount || 0), minutes: a.minutes + (d.offlineMinutes || 0), tracked: a.tracked + 1 };
  }, { count: 0, minutes: 0, tracked: 0 });

  const shown = events.filter((e) => (
    filter === 'all' ? true
      : filter === 'connection' ? e.type === 'online' || e.type === 'offline'
        : filter === 'automatic' ? e.type && e.type.startsWith('auto_')
          : e.type && e.type.startsWith('problem')
  ));

  const problems = (state && state.problems) || [];
  const since = state && asDate(state.problemSince);
  const critical = problems.some((p) => p.severity === 2);

  return (
    <section className="card-chrome overflow-hidden mb-4">
      <header className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2">
          <History size={ICON} /> Health History
        </h2>
        <span className="text-xs text-gray-500">Kept for 90 days · Belgrade time</span>
      </header>

      {/* Current state */}
      <div className="px-5 py-4 border-b border-white/[0.06]">
        {state === undefined ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : state === null ? (
          <p className="text-sm text-gray-500">No analytics for this device yet. It appears after the next scan.</p>
        ) : state.retired ? (
          <p className="text-sm text-gray-400">In the DELETED group - out of contract, so not tracked as a problem and no automatic actions. Everything else works as usual.</p>
        ) : state.inactive ? (
          <p className="text-sm text-gray-400">Inactive - offline for a long time, treated as stored and not as a problem.</p>
        ) : problems.length === 0 ? (
          <p className="text-sm text-green-400 inline-flex items-center gap-2"><CheckCircle2 size={ICON} /> No problems right now.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${critical ? 'bg-red-500/15 text-red-300 border-red-500/30' : 'bg-amber-500/15 text-amber-300 border-amber-500/30'}`}>
                {critical ? <ShieldAlert size={12} /> : <AlertTriangle size={12} />} {critical ? 'Critical' : 'Warning'}
              </span>
              {since && <span className="text-xs text-gray-500">since {since.toLocaleString()}</span>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {problems.map((p) => (
                <span
                  key={`${p.code}-${p.value}`}
                  className={`px-2 py-0.5 rounded-md text-[11px] border ${p.severity === 2 ? 'border-red-500/30 text-red-200 bg-red-500/[0.08]' : 'border-amber-500/30 text-amber-200 bg-amber-500/[0.08]'}`}
                >
                  {PROBLEM_LABEL[p.code] || p.code}
                  {problemValue(p) ? <span className="text-gray-400"> · {problemValue(p)}</span> : null}
                </span>
              ))}
            </div>
          </>
        )}
        {state && !state.inactive && <MuteControl mac={mac} group={state.group || ''} />}
      </div>

      {/* Last 30 days */}
      <div className="px-5 py-4 border-b border-white/[0.06]">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
          <p className="text-sm font-medium text-gray-200">Last 30 days</p>
          <p className="text-xs text-gray-500">
            {totals.tracked
              ? `Offline ${totals.count}× · ${minutesText(totals.minutes)} in total`
              : 'Days fill in as the device is tracked.'}
          </p>
        </div>
        <div className="grid gap-[2px]" style={{ gridTemplateColumns: `repeat(${DAYS}, minmax(0, 1fr))` }}>
          {keys.map((k) => {
            const d = days[k];
            const t = dayTone(d);
            const tip = d
              ? `${dayLabel(k)} ${t.label}\nOffline ${d.offlineCount || 0}× · ${minutesText(d.offlineMinutes || 0)}${(d.problemsSeen || []).length ? `\nProblems: ${d.problemsSeen.map((c) => PROBLEM_LABEL[c] || c).join(', ')}` : ''}`
              : `${dayLabel(k)} No data`;
            return <div key={k} title={tip} className={`h-7 rounded-[3px] ${t.cls} hover:ring-1 hover:ring-white/40`} />;
          })}
        </div>
        <div className="flex justify-between text-[11px] text-gray-500 mt-1">
          <span>{dayLabel(keys[0])}</span>
          <span>Today</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-gray-400">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-500/50" /> OK</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400/60" /> Short outage or problems</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500/60" /> Down 1 h+ or 5+ drops</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-white/[0.08]" /> No data</span>
        </div>
      </div>

      {/* Timeline */}
      <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap border-b border-white/[0.06]">
        <p className="text-sm font-medium text-gray-200">Timeline</p>
        <div className="flex gap-1.5">
          {FILTERS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${filter === id ? 'brand-pill' : 'bg-white/[0.02] border-white/[0.08] text-gray-400 hover:text-gray-200'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="px-5 py-4 text-sm text-gray-500">{loading ? 'Loading…' : 'Nothing recorded yet.'}</p>
      ) : (
        <ul className="divide-y divide-white/[0.06] max-h-[28rem] overflow-y-auto">
          {shown.map((e) => {
            const v = eventView(e);
            const at = asDate(e.at);
            return (
              <li key={e.id} className="px-5 py-2.5 flex items-center gap-3">
                <v.Icon size={ICON} className={`${v.tone} shrink-0`} />
                <span className="text-sm text-gray-100 truncate">{v.title}</span>
                {v.detail && <span className="text-xs text-gray-500 truncate">{v.detail}</span>}
                <span className="ml-auto text-xs text-gray-500 whitespace-nowrap">{at ? at.toLocaleString() : '—'}</span>
              </li>
            );
          })}
        </ul>
      )}
      {more && (
        <div className="px-5 py-3 border-t border-white/[0.06]">
          <button type="button" disabled={loading} onClick={() => loadEvents(cursor)} className="btn-secondary">
            {loading ? 'Loading…' : 'Load older'}
          </button>
        </div>
      )}
    </section>
  );
}
